const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

function sessionHeaders(session) {
    return {
        Authorization: session.accessToken,
        'Vendor-Id': session.vendorId,
        'Content-Type': 'application/json',
    };
}

export function normalizeUtilization(item) {
    return {
        ...item,
        timestamp: item.ts ?? item.timestamp,
    };
}

/**
 * Fetch all pages for a paginated admin observability endpoint.
 * @param {object} options
 * @param {'before'|'before_id'} [options.cursorParam='before']
 * @param {string} [options.nextCursorKey='next_cursor']  // or next_before_id
 * @param {(item: any) => any} [options.mapItem]
 */
export async function fetchAllPages(path, params, session, options = {}) {
    const {
        cursorParam = 'before',
        nextCursorKey = 'next_cursor',
        mapItem = (item) => item,
        limit = 1000,
        maxPages = 100,
        onPage,
    } = options;

    const items = [];
    let cursor = null;
    let pageCount = 0;
    let firstPageMeta = null;

    while (true) {
        pageCount += 1;
        const qs = new URLSearchParams({ ...params, limit: String(limit) });
        if (cursor != null) {
            qs.append(cursorParam, String(cursor));
        }

        const res = await fetch(`${API_BASE}${path}?${qs}`, {
            headers: sessionHeaders(session),
        });
        if (!res.ok) {
            throw new Error(`Failed to load ${path} (page ${pageCount}): ${res.status}`);
        }

        const body = await res.json();
        const pageItems = (body.items || []).map(mapItem);
        items.push(...pageItems);

        if (pageCount === 1) {
            firstPageMeta = {
                count: body.count || 0,
                has_more: body.has_more || false,
                next_cursor: body.next_cursor,
                refresh_cursor: body.refresh_cursor,
                next_before_id: body.next_before_id,
            };
        }

        if (typeof onPage === 'function') {
            onPage(pageCount, body);
        }

        if (!body.has_more || pageItems.length === 0) {
            break;
        }

        const next = body[nextCursorKey];
        if (next == null) {
            break;
        }
        cursor = next;

        if (pageCount >= maxPages) {
            break;
        }
    }

    return { items, firstPageMeta, pageCount };
}

export const getResourceUtilization = (hubId, from, to, session, options) =>
    fetchAllPages(
        `/device/${hubId}/resource-utilization`,
        { from: String(from), to: String(to) },
        session,
        {
            cursorParam: 'before',
            nextCursorKey: 'next_cursor',
            mapItem: normalizeUtilization,
            ...options,
        }
    );

export const getAiTelemetry = (hubId, from, to, session, options) =>
    fetchAllPages(
        `/device/${hubId}/ai-telemetry`,
        { from: String(from), to: String(to) },
        session,
        {
            cursorParam: 'before',
            nextCursorKey: 'next_cursor',
            ...options,
        }
    );

export async function getEvents(hubId, params, session, options = {}) {
    const { paginateAll = false, ...fetchOptions } = options;
    const query = {
        from: String(params.from ?? params.from_ms),
        to: String(params.to ?? params.to_ms),
        limit: String(params.limit ?? 100),
    };
    if (params.project) query.project = params.project;
    if (params.importance) query.importance = params.importance;
    if (params.before_id != null) query.before_id = String(params.before_id);

    if (!paginateAll) {
        const qs = new URLSearchParams(query);
        const res = await fetch(`${API_BASE}/device/${hubId}/events?${qs}`, {
            headers: sessionHeaders(session),
        });
        if (!res.ok) {
            throw new Error(`Failed to load events: ${res.status}`);
        }
        return res.json();
    }

    return fetchAllPages(
        `/device/${hubId}/events`,
        {
            from: query.from,
            to: query.to,
            ...(params.project ? { project: params.project } : {}),
            ...(params.importance ? { importance: params.importance } : {}),
        },
        session,
        {
            cursorParam: 'before_id',
            nextCursorKey: 'next_before_id',
            limit: Number(params.limit ?? 100),
            ...fetchOptions,
        }
    );
}

/**
 * Create an on-demand upload job. Returns { ok, status, body } so callers can
 * distinguish 429 / 409 / other 4xx without treating them as thrown network errors.
 */
export async function requestUpload(hubId, body, session) {
    const res = await fetch(`${API_BASE}/device/${hubId}/upload-request`, {
        method: 'POST',
        headers: sessionHeaders(session),
        body: JSON.stringify(body),
    });
    return parseAdminResponse(res);
}

/** Poll a single upload job. Same { ok, status, body } shape as requestUpload. */
export async function getUploadRequest(hubId, jobId, session) {
    const res = await fetch(`${API_BASE}/device/${hubId}/upload-request/${jobId}`, {
        headers: sessionHeaders(session),
    });
    return parseAdminResponse(res);
}

/** List recent upload jobs for a hub (historical + ongoing). */
export async function listUploadRequests(hubId, session) {
    const res = await fetch(`${API_BASE}/device/${hubId}/upload-request`, {
        headers: sessionHeaders(session),
    });
    return parseAdminResponse(res);
}

/**
 * List footage shares (owner-consented uploads of hub recordings to the
 * verification tool), newest first. Filters: status, hub_id, client_id;
 * pages with limit/offset. Same { ok, status, body } shape as requestUpload;
 * body is { items: [...] }.
 */
export async function listFootageShares(params, session) {
    const qs = new URLSearchParams();
    for (const key of ['status', 'hub_id', 'client_id', 'limit', 'offset']) {
        const value = params?.[key];
        if (value != null && String(value).trim() !== '') qs.set(key, String(value).trim());
    }
    const query = qs.toString();
    const res = await fetch(`${API_BASE}/footage-shares${query ? `?${query}` : ''}`, {
        headers: sessionHeaders(session),
    });
    return parseAdminResponse(res);
}

/** One footage share with its datasets. Same { ok, status, body } shape. */
export async function getFootageShare(shareId, session) {
    const res = await fetch(`${API_BASE}/footage-shares/${shareId}`, {
        headers: sessionHeaders(session),
    });
    return parseAdminResponse(res);
}

async function parseAdminResponse(res) {
    let body = null;
    const text = await res.text();
    if (text) {
        try {
            body = JSON.parse(text);
        } catch {
            body = { error: text };
        }
    }
    return { ok: res.ok, status: res.status, body };
}

export { API_BASE };
